import React, { useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  findPromotionDetail,
  getMetaRouteContext,
  resolveMetaTags
} from 'utils/metaTag'
import { stripTrailingSlash } from 'utils/routes'

/**
 * Component responsible for managing and rendering meta tags for
 * specific routes using React Helmet.
 *
 * This component determines which route is currently active, retrieves
 * associated metadata (e.g., title and description) from the Redux store,
 * and dynamically updates the document head.
 *
 * @component
 * @returns {JSX.Element} Meta tags for the current route.
 */
const MetaTags = () => {
  const location = useLocation()
  const { pathname } = location
  const { languageData, selectedLanguageCode } = useSelector(state => state.language)
  const {
    bonusDetail: bonusDetails = {},
    userBonusDetails = {}
  } = useSelector(state => state?.bonus || {})

  /**
   * CMS details containing metadata for the CMS pages.
   * This data is used to dynamically set meta titles and descriptions.
   * @typedef {Object} CMSDetails
   * @property {Object} metaTitle - Object containing meta titles for different languages.
   * @property {Object} metaDescription - Object containing meta descriptions for different languages.
   * @property {Object} title - Default title object with fallback values for different languages.
   */

  /** @type {CMSDetails} */
  const cmsDetails = useSelector(state => state?.cms?.cmsDetails || {})

  const DEFAULT_TITLE = process.env.REACT_APP_TENANT_DISPLAY_NAME || 'Casino'
  const DEFAULT_DESCRIPTION = 'Online Casino'

  const routeContext = useMemo(() => {
    return getMetaRouteContext(pathname)
  }, [pathname])

  // Promotion detail SEO may improve after the detail API resolves because the
  // response can provide a canonical slug that should win over the route id.
  const promotionDetail = useMemo(() => {
    if (routeContext?.routeType !== 'promotionDetail') {
      return null
    }

    return findPromotionDetail({
      routeIdentifier: routeContext?.params?.bonusId,
      userBonusId: routeContext?.params?.userBonusId,
      bonusDetailsById: bonusDetails,
      userBonusDetailsById: userBonusDetails,
      selectedLanguageCode
    })
  }, [bonusDetails, routeContext, selectedLanguageCode, userBonusDetails])

  const {
    title: translatedTitle,
    description: translatedDescription
  } = useMemo(() => {
    return resolveMetaTags({
      pathname,
      languageData,
      selectedLanguageCode,
      cmsDetails,
      promotionDetail,
      defaultTitle: DEFAULT_TITLE,
      defaultDescription: DEFAULT_DESCRIPTION
    })
  }, [pathname, languageData, selectedLanguageCode, cmsDetails, promotionDetail, DEFAULT_TITLE, DEFAULT_DESCRIPTION])

  const canonicalUrl = useMemo(() => {
    return `${window?.origin}${stripTrailingSlash(pathname)}`
  }, [pathname])

  return (
    <Helmet>
      <title>{translatedTitle}</title>
      <meta name='description' content={translatedDescription} />
      <link rel='canonical' href={canonicalUrl} />
    </Helmet>
  )
}

export default MetaTags
